import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { nanoid } from 'nanoid';

// Create the Axios instance with interceptor
const apiClient: AxiosInstance = axios.create();

// Add the interceptor to attach correlation ID
apiClient.interceptors.request.use(
  (config) => {
    const correlationId = nanoid();
    config.headers['x-correlation-id'] = correlationId;
    return config;
  },
  (error) => Promise.reject(error),
);

// GET request
async function get<T>(url: string): Promise<T> {
  try {
    if (!url) throw new Error('Missing Url');
    const resp: AxiosResponse<T> = await apiClient.get<string, AxiosResponse<T>>(url);
    return resp.data;
  } catch (error) {
    const err: AxiosError = error as AxiosError;
    return err?.response?.data as T;
  }
}

// POST request
async function post<T1, T2>(url: string, params: T1): Promise<T2> {
  if (!url) throw new Error('Missing Url');
  const resp: AxiosResponse<T2> = await apiClient.post(url, params);
  return resp.data;
}

// PUT request
async function put<T>(url: string, params: object): Promise<T | void> {
  try {
    const resp = await apiClient.put<T>(url, params);
    return resp.data;
  } catch (error) {
    console.error(error);
  }
}

// DELETE request
async function deleteRequest<T = void>(url: string): Promise<T | void> {
  try {
    const resp = await apiClient.delete<T>(url);
    return resp.data;
  } catch (error) {
    console.error(error);
  }
}

// PATCH request
async function patch<T>(url: string, params: object): Promise<T | void> {
  try {
    const resp = await apiClient.patch<T>(url, params);
    return resp.data;
  } catch (error) {
    console.error(error);
  }
}

// Export the functional API
const APIClient = {
  get,
  post,
  put,
  delete: deleteRequest,
  patch,
};

export default APIClient;
